import { useGlobalConfig } from '@/components/GlobalContext';

import { CraveErrorCode, CraveErrorType } from '@/enums/errorCodes';
import { Language } from '@/services/graphQL/api/generated/types_web';
import { getSystem } from '@/system';
import { CraveError } from '@/types';
import { getLocalization } from '@/utils/localization';

/**
 * This file contains the Error definitions based on documentation.
 * The error codes are used to identify the type of error that occurred
 * and are formatted as follows: [SystemPrefix]-[ErrorCode]. eg. A-1200.
 * The messages are used to display the error to the user.
 * The error codes are defined in the enums/errorCodes.ts file.
 *
 * Source: https://confluence.bell.corp.bce.ca/display/TEAMBMF/HTMLTV
 */

export const Errors = new Map<CraveErrorCode, CraveError>();

/**
 * RTE - Metadata (GraphQL)
 */
Errors.set(CraveErrorCode.SCREEN_NOT_READY_1001, {
  code: CraveErrorCode.SCREEN_NOT_READY_1001,
  type: CraveErrorType.APPLICATION,
  id: 'screen-not-ready-1001',
  retry: true,
});

Errors.set(CraveErrorCode.MEDIA_ID_NOT_FOUND_1002, {
  code: CraveErrorCode.MEDIA_ID_NOT_FOUND_1002,
  type: CraveErrorType.APPLICATION,
  id: 'media-id-not-found-1002',
  retry: true,
});

Errors.set(CraveErrorCode.LIVE_CHANNELS_UNAVAILABLE_1003, {
  code: CraveErrorCode.LIVE_CHANNELS_UNAVAILABLE_1003,
  type: CraveErrorType.APPLICATION,
  id: 'live-channels-unavailable-1003',
  retry: true,
});

/**
 * RTE - Playback (REST)
 */

/**
 * RTE - Cravings (REST)
 */
Errors.set(CraveErrorCode.INVALID_FORMAT_1400, {
  code: CraveErrorCode.INVALID_FORMAT_1400,
  type: CraveErrorType.APPLICATION,
  id: 'invalid-format-1400',
});

Errors.set(CraveErrorCode.MY_LIST_NOT_FOUND_1401, {
  code: CraveErrorCode.MY_LIST_NOT_FOUND_1401,
  type: CraveErrorType.APPLICATION,
  id: 'my-list-not-found-1401',
  retry: true,
});

Errors.set(CraveErrorCode.MEDIA_ID_NOT_FOUND_1402, {
  code: CraveErrorCode.MEDIA_ID_NOT_FOUND_1402,
  type: CraveErrorType.APPLICATION,
  id: 'media-id-not-found-1402',
});

Errors.set(CraveErrorCode.INVALID_FORMAT_1403, {
  code: CraveErrorCode.INVALID_FORMAT_1403,
  type: CraveErrorType.APPLICATION,
  id: 'invalid-format-1403',
});

Errors.set(CraveErrorCode.INTERNAL_SERVER_ERROR_1405, {
  code: CraveErrorCode.INTERNAL_SERVER_ERROR_1405,
  type: CraveErrorType.SERVER,
  id: 'internal-server-error-1405',
});

/**
 * RTE - User Management (REST)
 */
Errors.set(CraveErrorCode.BAD_REQUEST_2010, {
  code: CraveErrorCode.BAD_REQUEST_2010,
  type: CraveErrorType.SERVER,
  id: 'bad-request-2010',
});

Errors.set(CraveErrorCode.INTERNAL_SERVER_ERROR_2012, {
  code: CraveErrorCode.INTERNAL_SERVER_ERROR_2012,
  type: CraveErrorType.SERVER,
  id: 'internal-server-error-2012',
});

Errors.set(CraveErrorCode.INTERNAL_SERVER_ERROR_2015, {
  code: CraveErrorCode.INTERNAL_SERVER_ERROR_2015,
  type: CraveErrorType.SERVER,
  id: 'internal-server-error-2015',
});

Errors.set(CraveErrorCode.INTERNAL_SERVER_ERROR_2024, {
  code: CraveErrorCode.INTERNAL_SERVER_ERROR_2024,
  type: CraveErrorType.SERVER,
  id: 'internal-server-error-2024',
});

Errors.set(CraveErrorCode.BAD_REQUEST_2025, {
  code: CraveErrorCode.BAD_REQUEST_2025,
  type: CraveErrorType.SERVER,
  id: 'bad-request-2025',
});

/**
 * Jasper
 */

/**
 * Minor systems (Smart ID, OneTrust, Optimizely, ads, analytics products, etc)
 */

/**
 * Generic Error List
 */
Errors.set(CraveErrorCode.NO_NETWORK_CONNECTION_4001, {
  code: CraveErrorCode.NO_NETWORK_CONNECTION_4001,
  type: CraveErrorType.NETWORK,
  id: 'no-network-connection-4001',
  retry: false,
});

Errors.set(CraveErrorCode.GENERIC_ERROR_4002, {
  code: CraveErrorCode.GENERIC_ERROR_4002,
  type: CraveErrorType.GENERIC,
  id: 'generic-error-4002',
});

Errors.set(CraveErrorCode.CONNECTION_ERROR_4003, {
  code: CraveErrorCode.CONNECTION_ERROR_4003,
  type: CraveErrorType.SERVER,
  id: 'connection-error-4003',
  retry: true,
});

export const getCraveError = (errorCode: CraveErrorCode, userLanguage: Language) => {
  const system = getSystem();

  // Copy so callers holding a previous result aren't mutated by later calls
  const craveError = { ...Errors.get(errorCode) } as CraveError;

  const errorTitle = (getLocalization(userLanguage) as { [key: string]: any })['errors'][
    craveError.id
  ]['error-title'];
  craveError.title = errorTitle;

  const errorMessage = (getLocalization(userLanguage) as { [key: string]: any })[
    'errors'
  ][craveError.id]['error-message'];
  craveError.message = errorMessage ? errorMessage : '';

  const errorPrefix = system.getErrorCodePrefix();
  craveError.errorCode = `${errorPrefix}-${craveError.code}`;

  return craveError;
};
